import { executionArtifactLineId } from '../families/execution-artifact.js';
import type { ComparisonSpec } from '../primitives/comparison.js';
import { artifactTerminalHeight, argumentHeight, fittedHeight, sequenceHeight } from './heights.js';
import type { GoLesson } from './types.js';

const TRUTH_FILE = 'go-mutex-fix.json';
const GO = 'go1.24.4 windows/amd64';

/**
 * Where the lock goes.
 *
 * The captures in kiln/content/truth/go-mutex-fix.json are deliberately split:
 * the race detector proves the shown withdraw() really is racy but cannot fix a
 * number, so every number comes from the channel-pinned run (80), the five
 * unforced runs of the fixed version (70), and the contention benchmark.
 */
const tradeOffFigure = (width: number, height: number): ComparisonSpec => ({
        id: 'go-mutex-fix.trade-off',
        family: 'comparison',
        view: 'table',
        viewport: { width, height },
        subject: 'a 10 ms section',
        question: 'What does serialising the section cost as goroutines arrive?',
        options: [
          { id: 'unlocked', label: 'no mutex', detail: 'sections overlap' },
          { id: 'locked', label: 'mutex', detail: 'sections queue' },
        ],
        criteria: [
          { id: 'two', label: '2 goroutines', unit: 'ms', first: 10, second: 21, better: 'lower' },
          { id: 'three', label: '3 goroutines', unit: 'ms', first: 10, second: 30, better: 'lower' },
          { id: 'four', label: '4 goroutines', unit: 'ms', first: 10, second: 41, better: 'lower' },
          { id: 'five', label: '5 goroutines', unit: 'ms', first: 10, second: 51, better: 'lower', decisive: true },
        ],
        verdictOptionId: 'unlocked',
        verdictReason: 'Each extra goroutine adds one whole section, because only one of them may be inside at a time.',
        title: 'Wall clock for the same program, with and without the lock',
        subtitle: 'This is the bill, not the verdict: the earlier scenes are what the unlocked column actually costs.',
        provenance: `Captured ${GO} · truth/${TRUTH_FILE} · contention · wall-clock measurements, ±2 ms of scheduler overhead`,
      });

export const goMutexFix: GoLesson = {
  id: 'go-mutex-fix',
  module: '07 · Concurrency',
  title: 'Where the lock goes',
  premise: 'A mutex orders the statements between Lock and Unlock. It has no opinion about the ones above.',
  toolchain: GO,
  scenes: [
    {
      id: 'symptom',
      beat: 'symptom',
      stageLabel: 'SYMPTOM · GO RUN -RACE .',
      heading: 'The lock is right there, and it still raced.',
      subtitle: 'withdraw() takes mu before every write to balance. The detector still finds one race.',
      figureReason:
        'The claim is a detector finding, and a detector finding is a document: two addresses, two goroutines, one exit code. The terminal artifact presents it without rewriting a byte.',
      code: {
        caption: 'main.go · withdraw()',
        text: [
          'func withdraw(amount int) {',
          '\tb := balance // outside the lock',
          '\tb -= amount',
          '\tmu.Lock()',
          '\tbalance = b // inside the lock',
          '\tmu.Unlock()',
          '}',
        ].join('\n'),
      },
      evidence: {
        caption: 'go run -race .',
        text: [
          'WARNING: DATA RACE',
          'Read at 0x000140113248 by goroutine 7:',
          '  main.withdraw()',
          'Previous write at 0x000140113248 by goroutine 8:',
          '  main.withdraw()',
          'Found 1 data race(s)',
          'exit status 66',
        ].join('\n'),
      },
      determinism:
        'The race is reported on every run; which goroutine wins (and therefore the printed balance) is not deterministic, which is why the lesson quotes the forced program for the number.',
      truth: { file: TRUTH_FILE, capture: 'partial-lock-idiomatic' },
      quoted: [
        '==================',
        'WARNING: DATA RACE',
        'Read at 0x000140113248 by goroutine 7:',
        '  main.withdraw()',
        'Previous write at 0x000140113248 by goroutine 8:',
        'expected=70 actual=70',
        'Found 1 data race(s)',
        'exit status 66',
      ],
      spec: (width) => ({
        id: 'go-mutex-fix.symptom',
        family: 'execution-artifact',
        view: 'artifact',
        artifactKind: 'terminal',
        surface: 'terminal',
        viewport: { width, height: artifactTerminalHeight(width, 12) },
        context: { tabLabel: 'go run -race', exitCode: 66 },
        title: 'One race, two goroutines · frame paths omitted',
        lines: [
          { id: 'command', kind: 'command', text: 'go run -race .' },
          { id: 'rule-top', kind: 'muted', text: '==================' },
          { id: 'warning', kind: 'evidence', text: 'WARNING: DATA RACE' },
          { id: 'read', kind: 'output', text: 'Read at 0x000140113248 by goroutine 7:' },
          { id: 'read-frame', kind: 'output', text: '  main.withdraw()' },
          { id: 'write', kind: 'output', text: 'Previous write at 0x000140113248 by goroutine 8:' },
          { id: 'write-frame', kind: 'output', text: '  main.withdraw()' },
          { id: 'rule-bottom', kind: 'muted', text: '==================' },
          { id: 'balance', kind: 'output', text: 'expected=70 actual=70' },
          { id: 'found', kind: 'evidence', text: 'Found 1 data race(s)' },
          { id: 'exit', kind: 'muted', text: 'exit status 66' },
        ],
        focusLineId: executionArtifactLineId('found'),
        provenance: `Captured ${GO} · truth/${TRUTH_FILE} · partial-lock-idiomatic · every line verbatim; the machine-specific source paths under each frame are omitted, and the printed balance is not reproducible`,
      }),
    },
    {
      id: 'mechanism',
      beat: 'mechanism',
      stageLabel: 'MECHANISM · FORCED TRACE',
      heading: 'Both goroutines read 100 before either wrote.',
      subtitle: 'Pinning the schedule with channels replays one real losing interleaving on every run.',
      figureReason:
        'The failure is entirely an ordering fact: which read happened before which write. A sequence gives each participant a lane and each event a position, so the two reads visibly land on the same value.',
      code: {
        caption: 'main.go · goroutine A, schedule pinned',
        text: [
          '\tgo func() { // goroutine A: withdraw(10)',
          '\t\tdefer wg.Done()',
          '\t\tb := balance // read, outside the lock',
          '\t\tclose(aRead)',
          '\t\t<-bRead // let B read the same value first',
          '\t\tb -= 10',
          '\t\tmu.Lock()',
          '\t\tbalance = b // write, inside the lock',
          '\t\tmu.Unlock()',
          '\t\tclose(aWrote)',
          '\t}()',
        ].join('\n'),
      },
      evidence: { caption: 'go run .', text: 'expected=70 actual=80' },
      determinism:
        'The schedule is pinned with channels, so this output is identical on every run (verified 3 consecutive runs). Same technique as poc/go-truth/forced/main.go.',
      truth: { file: TRUTH_FILE, capture: 'partial-lock-forced' },
      quoted: ['expected=70 actual=80'],
      spec: (width) => ({
        id: 'go-mutex-fix.mechanism',
        family: 'concurrency-sequence',
        view: 'sequence',
        viewport: { width, height: sequenceHeight(width, 6) },
        participants: [
          { id: 'a', label: 'A -10', role: 'actor' },
          { id: 'balance', label: 'balance', role: 'storage' },
          { id: 'mu', label: 'mu', role: 'runtime' },
          { id: 'b', label: 'B -20', role: 'actor' },
        ],
        events: [
          { id: 'a-read', from: 'a', to: 'balance', label: 'read 100', kind: 'call' },
          { id: 'b-read', from: 'b', to: 'balance', label: 'read 100', kind: 'call' },
          { id: 'a-lock', from: 'a', to: 'mu', label: 'Lock()', kind: 'call' },
          { id: 'a-write', from: 'a', to: 'balance', label: 'write 90', kind: 'call' },
          { id: 'b-lock', from: 'b', to: 'mu', label: 'Lock()', kind: 'call' },
          { id: 'b-write', from: 'b', to: 'balance', label: 'write 80', kind: 'error' },
        ],
        focusEventId: 'b-write',
        provenance: `Captured ${GO} · truth/${TRUTH_FILE} · partial-lock-forced · balance starts at 100, the goroutines withdraw 10 and 20, and the run prints expected=70 actual=80`,
      }),
    },
    {
      id: 'fix',
      beat: 'fix',
      stageLabel: 'FIX · ONE SECTION',
      heading: 'The second goroutine has to wait to read.',
      subtitle: 'With Lock above the read, the whole read-modify-write becomes one indivisible step.',
      figureReason:
        'The same assertion type as the scene before it — what happened in what order — so the same figure, with one relation changed. Holding the grammar fixed is what makes the changed lane legible.',
      code: {
        caption: 'main.go · withdraw(), fixed',
        text: [
          'func withdraw(amount int) {',
          '\tmu.Lock()',
          '\tdefer mu.Unlock()',
          '\tb := balance',
          '\tb -= amount',
          '\tbalance = b',
          '}',
        ].join('\n'),
      },
      evidence: {
        caption: 'go run .',
        text: [
          'run 1: expected=70 actual=70',
          'run 2: expected=70 actual=70',
          'run 3: expected=70 actual=70',
          'run 4: expected=70 actual=70',
          'run 5: expected=70 actual=70',
        ].join('\n'),
      },
      determinism: '5 consecutive runs, unforced, all 70. No -race finding.',
      truth: { file: TRUTH_FILE, capture: 'full-lock' },
      quoted: ['run 1: expected=70 actual=70', 'run 5: expected=70 actual=70'],
      spec: (width) => ({
        id: 'go-mutex-fix.fix',
        family: 'concurrency-sequence',
        view: 'sequence',
        viewport: { width, height: sequenceHeight(width, 7) },
        participants: [
          { id: 'a', label: 'A -10', role: 'actor' },
          { id: 'balance', label: 'balance', role: 'storage' },
          { id: 'mu', label: 'mu', role: 'runtime' },
          { id: 'b', label: 'B -20', role: 'actor' },
        ],
        events: [
          { id: 'a-lock', from: 'a', to: 'mu', label: 'Lock()', kind: 'call' },
          { id: 'b-blocked', from: 'b', to: 'b', label: 'blocked', kind: 'wait' },
          { id: 'a-read', from: 'a', to: 'balance', label: 'read 100', kind: 'call' },
          { id: 'a-write', from: 'a', to: 'balance', label: 'write 90', kind: 'call' },
          { id: 'handover', from: 'mu', to: 'b', label: 'Unlock()', kind: 'return' },
          { id: 'b-read', from: 'b', to: 'balance', label: 'read 90', kind: 'call' },
          { id: 'b-write', from: 'b', to: 'balance', label: 'write 70', kind: 'call' },
        ],
        focusEventId: 'b-read',
        provenance: `Captured ${GO} · truth/${TRUTH_FILE} · full-lock · one of the two legal orders; the other reads 100 then 80 and also ends at 70, which is what all five unforced runs print`,
      }),
    },
    {
      id: 'evidence',
      beat: 'evidence',
      stageLabel: 'EVIDENCE · FIVE RUNS',
      heading: 'Five runs, nothing forced, the same answer.',
      subtitle: 'The scheduler is free to interleave again; the invariant no longer depends on how it does.',
      figureReason:
        'Five lines that are identical is a claim only the unedited record can make; a drawing of five equal bars would be an author asserting it. The code-runner surface was the first choice and was rejected: it prints a PostgreSQL footer under Go source.',
      code: {
        caption: 'main.go · run loop',
        text: [
          '\tfor run := 1; run <= 5; run++ {',
          '\t\tbalance = 100',
          '\t\tvar wg sync.WaitGroup',
          '\t\twg.Add(2)',
          '\t\tgo func() { defer wg.Done(); withdraw(10) }()',
          '\t\tgo func() { defer wg.Done(); withdraw(20) }()',
          '\t\twg.Wait()',
          '\t}',
        ].join('\n'),
      },
      evidence: {
        caption: 'go run .',
        text: [
          'run 1: expected=70 actual=70',
          'run 2: expected=70 actual=70',
          'run 3: expected=70 actual=70',
          'run 4: expected=70 actual=70',
          'run 5: expected=70 actual=70',
        ].join('\n'),
      },
      determinism: '5 consecutive runs, unforced, all 70. No -race finding.',
      truth: { file: TRUTH_FILE, capture: 'full-lock' },
      quoted: [
        'run 1: expected=70 actual=70',
        'run 2: expected=70 actual=70',
        'run 3: expected=70 actual=70',
        'run 4: expected=70 actual=70',
        'run 5: expected=70 actual=70',
      ],
      spec: (width) => ({
        id: 'go-mutex-fix.evidence',
        family: 'execution-artifact',
        view: 'artifact',
        artifactKind: 'terminal',
        surface: 'terminal',
        viewport: { width, height: artifactTerminalHeight(width, 6) },
        context: { tabLabel: 'go run', exitCode: 0 },
        title: 'Five unforced runs, one answer',
        lines: [
          { id: 'command', kind: 'command', text: 'go run .' },
          { id: 'run-1', kind: 'output', text: 'run 1: expected=70 actual=70' },
          { id: 'run-2', kind: 'output', text: 'run 2: expected=70 actual=70' },
          { id: 'run-3', kind: 'output', text: 'run 3: expected=70 actual=70' },
          { id: 'run-4', kind: 'output', text: 'run 4: expected=70 actual=70' },
          { id: 'run-5', kind: 'evidence', text: 'run 5: expected=70 actual=70' },
        ],
        focusLineId: executionArtifactLineId('run-5'),
        provenance: `Captured ${GO} · truth/${TRUTH_FILE} · full-lock · stdout retained verbatim, 5 identical unforced runs`,
      }),
    },
    {
      id: 'trade-off',
      beat: 'trade-off',
      stageLabel: 'TRADE-OFF · WHAT IT COSTS',
      heading: 'The lock costs exactly the concurrency it removes.',
      subtitle: 'Five goroutines, the same 10 ms section each: 51 ms serialised against 10 ms in parallel.',
      figureReason:
        'Two versions of one program, measured on the same axis, with a direction of preference per row. That is a comparison, and its verdict is computed from the measurements rather than asserted by the author.',
      code: {
        caption: 'main.go · contention benchmark',
        text: [
          '\t\tgo func() {',
          '\t\t\tdefer wg.Done()',
          '\t\t\tif lock {',
          '\t\t\t\tmu.Lock()',
          '\t\t\t\tdefer mu.Unlock()',
          '\t\t\t}',
          '\t\t\ttime.Sleep(section)',
          '\t\t}()',
        ].join('\n'),
      },
      evidence: {
        caption: 'go run .',
        text: [
          '1 goroutines  mutex= 10ms  no mutex= 10ms',
          '2 goroutines  mutex= 21ms  no mutex= 10ms',
          '3 goroutines  mutex= 30ms  no mutex= 10ms',
          '4 goroutines  mutex= 41ms  no mutex= 10ms',
          '5 goroutines  mutex= 51ms  no mutex= 10ms',
        ].join('\n'),
      },
      determinism:
        'Wall-clock, so ±2ms of scheduler overhead: measured 10/21/30/41/51ms against the ideal 10/20/30/40/50ms. The no-mutex column is 10ms at every size — that is the concurrency the lock costs.',
      truth: { file: TRUTH_FILE, capture: 'contention' },
      quoted: [
        '1 goroutines  mutex= 10ms  no mutex= 10ms',
        '2 goroutines  mutex= 21ms  no mutex= 10ms',
        '3 goroutines  mutex= 30ms  no mutex= 10ms',
        '4 goroutines  mutex= 41ms  no mutex= 10ms',
        '5 goroutines  mutex= 51ms  no mutex= 10ms',
      ],
      spec: (width) => tradeOffFigure(width, fittedHeight((height) => tradeOffFigure(width, height), 500)),
    },
    {
      id: 'production',
      beat: 'production',
      stageLabel: 'PRODUCTION · IT DEADLOCKED',
      heading: 'Withdraw held the lock, then called Balance.',
      subtitle: 'Balance takes the same mutex, and a sync.Mutex is not reentrant, so the goroutine waits.',
      figureReason:
        'The runtime prints the answer as a stack: which function is blocked, and which two frames below it already hold what it is waiting for. The artifact keeps that stack as the runtime wrote it.',
      code: {
        caption: 'main.go · the innocent-looking guard',
        text: [
          'func Withdraw(amount int) {',
          '\tmu.Lock()',
          '\tdefer mu.Unlock()',
          '\tif Balance() < amount { // takes the same lock again',
          '\t\treturn',
          '\t}',
          '\tbalance -= amount',
          '}',
        ].join('\n'),
      },
      evidence: {
        caption: 'go run .',
        text: [
          'fatal error: all goroutines are asleep - deadlock!',
          '',
          'goroutine 1 [sync.Mutex.Lock]:',
          'sync.(*Mutex).Lock(...)',
          'main.Balance()',
          'main.Withdraw(0xa)',
          'main.main()',
          'exit status 2',
        ].join('\n'),
      },
      determinism: 'Deterministic: the goroutine blocks on a lock it already holds, so the runtime always reports the deadlock and exits 2.',
      truth: { file: TRUTH_FILE, capture: 'reentrant-deadlock' },
      quoted: [
        'fatal error: all goroutines are asleep - deadlock!',
        'goroutine 1 [sync.Mutex.Lock]:',
        'sync.(*Mutex).Lock(...)',
        'main.Balance()',
        'main.Withdraw(0xa)',
        'main.main()',
        'exit status 2',
      ],
      spec: (width) => ({
        id: 'go-mutex-fix.production',
        family: 'execution-artifact',
        view: 'artifact',
        artifactKind: 'terminal',
        surface: 'terminal',
        viewport: { width, height: artifactTerminalHeight(width, 8) },
        context: { tabLabel: 'go run', exitCode: 2 },
        title: 'Blocked on a lock it holds · frame paths omitted',
        lines: [
          { id: 'command', kind: 'command', text: 'go run .' },
          { id: 'fatal', kind: 'output', text: 'fatal error: all goroutines are asleep - deadlock!' },
          { id: 'goroutine', kind: 'output', text: 'goroutine 1 [sync.Mutex.Lock]:' },
          { id: 'lock-frame', kind: 'output', text: 'sync.(*Mutex).Lock(...)' },
          { id: 'balance-frame', kind: 'evidence', text: 'main.Balance()' },
          { id: 'withdraw-frame', kind: 'output', text: 'main.Withdraw(0xa)' },
          { id: 'main-frame', kind: 'output', text: 'main.main()' },
          { id: 'exit', kind: 'muted', text: 'exit status 2' },
        ],
        focusLineId: executionArtifactLineId('balance-frame'),
        provenance: `Captured ${GO} · truth/${TRUTH_FILE} · reentrant-deadlock · every line verbatim; the runtime and source path lines beneath each frame are omitted`,
      }),
    },
    {
      id: 'model',
      beat: 'model',
      stageLabel: 'MODEL · WHY IT FOLLOWS',
      heading: 'The critical section is the invariant, not the write.',
      subtitle: 'Whatever the invariant spans, the lock has to span too — reads included, both ends.',
      figureReason:
        'The closing claim is an implication, so it earns a derivation: each line an expression, each expression a rationale, and the last line the only one marked as a conclusion.',
      code: {
        caption: 'main.go · the partial lock, forced',
        text: [
          '\t\tb := balance // read, outside the lock',
          '\t\tb -= 10',
          '\t\tmu.Lock()',
          '\t\tbalance = b // write, inside the lock',
          '\t\tmu.Unlock()',
        ].join('\n'),
      },
      evidence: { caption: 'go run . · partial lock, forced', text: 'expected=70 actual=80' },
      determinism: 'The schedule is pinned with channels, so this output is identical on every run (verified 3 consecutive runs). Same technique as poc/go-truth/forced/main.go.',
      truth: { file: TRUTH_FILE, capture: 'partial-lock-forced' },
      quoted: ['expected=70 actual=80'],
      spec: (width) => ({
        id: 'go-mutex-fix.model',
        family: 'complexity-argument',
        view: 'annotated-argument',
        viewport: { width, height: argumentHeight(width, 4) },
        claim: 'One section: read, modify, write',
        steps: [
          {
            id: 'operations',
            kind: 'given',
            expression: 'withdraw = read, subtract, write',
            rationale: 'Three operations on one variable, and nothing may observe it between the first and the last.',
          },
          {
            id: 'write-only',
            kind: 'transform',
            expression: 'lock the write only',
            rationale: 'Both goroutines read 100 before either locks, so the mutex faithfully orders two stale writes.',
          },
          {
            id: 'whole-section',
            kind: 'transform',
            expression: 'lock the whole sequence',
            rationale: 'The second goroutine blocks before its read, so it reads what the first one wrote.',
          },
          {
            id: 'conclusion',
            kind: 'conclusion',
            expression: 'the section spans the invariant',
            rationale: 'Forced partial lock: actual=80. Whole-section lock, five unforced runs: actual=70.',
          },
        ],
        focusStepId: 'conclusion',
        provenance: `Captured ${GO} · truth/${TRUTH_FILE} · partial-lock-forced and full-lock · both quoted numbers are printed values`,
      }),
    },
  ],
};
